/**
 * English prepositions (+ common phrasal-verb particles).
 * Preposition column stores the English headword with those words marked in [brackets],
 * e.g. "(be) kind to, (be) fed up with" → "(be) kind [to], (be) fed [up] [with]".
 */

/** Simple one-word prepositions / particles (lowercase). */
export const SIMPLE_PREPOSITIONS = [
  'aboard', 'about', 'above', 'across', 'after', 'against', 'along', 'alongside',
  'amid', 'amidst', 'among', 'amongst', 'anti', 'around', 'as', 'astride', 'at',
  'atop', 'before', 'behind', 'below', 'beneath', 'beside', 'besides', 'between',
  'beyond', 'but', 'by', 'circa', 'concerning', 'considering', 'despite', 'down',
  'during', 'except', 'excluding', 'following', 'for', 'from', 'in', 'including',
  'inside', 'into', 'like', 'minus', 'near', 'nearby', 'next', 'notwithstanding',
  'of', 'off', 'on', 'onto', 'opposite', 'out', 'outside', 'over', 'past', 'per',
  'plus', 'regarding', 'round', 'save', 'since', 'than', 'through', 'throughout',
  'till', 'to', 'toward', 'towards', 'under', 'underneath', 'unlike', 'until',
  'unto', 'up', 'upon', 'versus', 'via', 'with', 'within', 'without', 'worth',
  // Common adverbial particles used with phrasal verbs
  'away', 'back', 'forth', 'forward', 'aside', 'apart',
];

/**
 * Multi-word prepositions (longest first for matching).
 * Used by isKnownPreposition; marking prefers per-word [up] [with] style.
 */
export const MULTIWORD_PREPOSITIONS = [
  'in accordance with',
  'in addition to',
  'in front of',
  'in place of',
  'in spite of',
  'in terms of',
  'on account of',
  'on behalf of',
  'on top of',
  'with regard to',
  'with respect to',
  'as far as',
  'as well as',
  'because of',
  'by means of',
  'by way of',
  'due to',
  'except for',
  'in case of',
  'in favour of',
  'in favor of',
  'in lieu of',
  'instead of',
  'next to',
  'out of',
  'outside of',
  'owing to',
  'prior to',
  'rather than',
  'such as',
  'up to',
  'according to',
  'ahead of',
  'apart from',
  'aside from',
  'away from',
  'close to',
  'depending on',
  'far from',
  'inside of',
  'near to',
  'other than',
  'regardless of',
  'thanks to',
].sort((a, b) => b.length - a.length);

const SIMPLE_SET = new Set(SIMPLE_PREPOSITIONS);
const PREPOSITION_SET = new Set([
  ...SIMPLE_PREPOSITIONS,
  ...MULTIWORD_PREPOSITIONS,
]);

function normalizePrepCandidate(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[’']/g, "'");
}

/** True if the text is a known preposition / particle. */
export function isKnownPreposition(text) {
  const n = normalizePrepCandidate(text);
  if (!n) return false;
  return PREPOSITION_SET.has(n);
}

/** Remove existing [brackets] so we can re-mark consistently. */
function unwrapBrackets(text) {
  return String(text || '').replace(/\[([^\[\]]*)\]/g, '$1');
}

/**
 * Particles / ambiguous words: only mark when trailing (or whole headword),
 * to avoid e.g. "high net worth individuals" → …[worth]…
 */
const TRAILING_ONLY = new Set([
  'as', 'but', 'like', 'than', 'next', 'worth', 'plus', 'past', 'round', 'save',
  'since', 'per', 'back', 'following', 'including', 'excluding', 'concerning',
  'considering', 'regarding', 'versus', 'unlike', 'anti', 'forward', 'forth',
  'aside', 'apart', 'nearby', 'notwithstanding',
]);

function looksLikeSentence(text) {
  const rest = String(text || '').trim().toLowerCase();
  if (!rest) return false;
  return /[?]/.test(rest)
    || /\.\s*[a-z]/.test(rest)
    || /^(can|did|do|does|is|are|was|were|i'll|i'|you|we|they|he|she)\b/i.test(rest)
    || rest.split(/\s+/).length > 10;
}

/**
 * Tokenize a headword segment, keeping parentheticals like (be) as one token.
 * @returns {{ raw: string, bare: string, paren: boolean }[]}
 */
function tokenizeSegment(segment) {
  const s = String(segment || '').trim();
  if (!s) return [];
  const tokens = [];
  const re = /(\([^)]*\)|\S+)/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    const raw = m[1];
    const paren = raw.startsWith('(') && raw.endsWith(')');
    const bare = normalizePrepCandidate(
      paren ? raw.slice(1, -1) : raw.replace(/[.,;:!?…]+$/g, '')
    );
    tokens.push({ raw, bare, paren });
  }
  return tokens;
}

/**
 * Mark known prepositions/particles in one comma-segment.
 * Core preps anywhere: "apply to join…" → "apply [to] join…"
 * Ambiguous/trailing-only words only at the end: "look like" → "look [like]"
 */
function markSegment(segment) {
  const leading = segment.match(/^\s*/)?.[0] || '';
  const trailingWs = segment.match(/\s*$/)?.[0] || '';
  const tokens = tokenizeSegment(segment);
  if (!tokens.length) return { text: segment, marked: false };

  const markIdx = new Set();

  // Mid-phrase: clear teaching prepositions (to, in, on, of, …)
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i];
    if (t.paren || !t.bare || !SIMPLE_SET.has(t.bare)) continue;
    if (TRAILING_ONLY.has(t.bare)) continue;
    markIdx.add(i);
  }

  // Trailing run: also mark particles / ambiguous words at the end
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i];
    if (t.paren) break;
    if (!t.bare || !SIMPLE_SET.has(t.bare)) break;
    markIdx.add(i);
  }

  if (!markIdx.size) {
    return { text: segment, marked: false };
  }

  const parts = tokens.map((t, i) => {
    if (!markIdx.has(i)) return t.raw;
    const inner = t.raw.replace(/[.,;:!?…]+$/g, '').replace(/^\[|\]$/g, '');
    const punct = t.raw.match(/[.,;:!?…]+$/)?.[0] || '';
    return `[${inner}]${punct}`;
  });

  return { text: leading + parts.join(' ') + trailingWs, marked: true };
}

/**
 * Build Preposition-column value from an English headword.
 * Marks known prepositions/particles in [brackets] (mid-phrase and trailing).
 * @returns {string|null} e.g. "(be) kind [to], (be) fed [up] [with]" — null if none
 */
export function extractPrepositionFromEnglish(english) {
  if (typeof english !== 'string' || !english.trim()) return null;
  if (looksLikeSentence(english)) return null;

  const unwrapped = unwrapBrackets(english.trim());
  // Split on commas that separate alternative phrases (not inside parentheses)
  const segments = [];
  let buf = '';
  let depth = 0;
  for (let i = 0; i < unwrapped.length; i += 1) {
    const ch = unwrapped[i];
    if (ch === '(') depth += 1;
    if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0) {
      segments.push(buf);
      buf = '';
      continue;
    }
    buf += ch;
  }
  segments.push(buf);

  let anyMarked = false;
  const marked = segments.map((seg) => {
    const result = markSegment(seg);
    if (result.marked) anyMarked = true;
    return result.text;
  });

  if (!anyMarked) return null;
  const joined = marked.length > 1
    ? marked.map((s) => s.trim()).join(', ')
    : marked[0];
  return joined.trim() || null;
}

/**
 * Set `preposition` from `english` (marked form). Clears when none match.
 * Does not change `english`.
 */
export function applyPrepositionFromEnglish(row) {
  if (!row || typeof row !== 'object') return row;
  if (typeof row.english !== 'string') return row;
  const prep = extractPrepositionFromEnglish(row.english);
  if ((row.preposition || null) === (prep || null)) return row;
  return { ...row, preposition: prep };
}

/**
 * Bracketed preposition/particle tokens from a Preposition column value.
 * e.g. "(be) kind [to], (be) fed [up] [with]" → ["to", "up", "with"]
 * @returns {string[]}
 */
export function extractPrepositionTokens(prepositionField) {
  const src = String(prepositionField || '').trim();
  if (!src || !src.includes('[')) return [];
  const tokens = [];
  const re = /\[([^\[\]]+)\]/g;
  let m;
  while ((m = re.exec(src)) !== null) {
    const inner = String(m[1] || '').trim();
    if (inner) tokens.push(inner);
  }
  return tokens;
}

/** True when the Preposition column has no usable bracketed prep. */
export function hasMissingPreposition(row) {
  return extractPrepositionTokens(row?.preposition).length === 0;
}

/**
 * Word matches a library prep filter (case-insensitive token in brackets).
 * Empty selectedPrep → no prep filter.
 */
export function wordMatchesPrepositionToken(row, selectedPrep) {
  const wanted = normalizePrepCandidate(selectedPrep);
  if (!wanted) return true;
  return extractPrepositionTokens(row?.preposition)
    .some((token) => normalizePrepCandidate(token) === wanted);
}

/**
 * Drill helpers for Looking Back → Prepositions mode.
 * Source is the Preposition column value, e.g. "pay back [with] interest".
 * @returns {{ prompt: string, answers: string[] } | null}
 */
export function getPrepositionDrill(prepositionField) {
  const answers = extractPrepositionTokens(prepositionField);
  if (!answers.length) return null;
  const src = String(prepositionField || '').trim();
  const prompt = src.replace(/\[([^\[\]]+)\]/g, '____');
  return { prompt, answers };
}

/** Normalize typed prep answer: lowercase, commas → spaces, collapse whitespace. */
export function normalizePrepositionAnswer(text) {
  return String(text || '')
    .trim()
    .toLowerCase()
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ');
}

/** True if user typed the bracketed preposition(s) in order. */
export function checkPrepositionAnswer(userInput, answers) {
  if (!answers?.length) return false;
  const expected = normalizePrepositionAnswer(answers.join(' '));
  const got = normalizePrepositionAnswer(userInput);
  return !!got && got === expected;
}
